import React, { useCallback, useMemo } from 'react';
import { AllocationName } from 'api';
import { ALLOCATIONS_FINAL, ALLOCATIONS_PUBLISHED } from 'common/constants/allocations';
import { VALIDATING } from 'common/constants/general';
import { ANALYST_VALUE, FIRM_ADMIN_VALUE, FUND_ADMIN_VALUE } from 'common/constants/user';
import { ShowDialog } from 'common/types/dialogContext';
import { QueryData, QueryError, Refetch } from 'common/types/services';
import { User } from 'common/types/store';
import { ValuationByAllocation } from 'common/types/valuation';
import { CenterAlignedParagraph } from 'components';
import { ConfirmationDialog } from 'components/Dialogs';
import {
  MAKE_FINAL_ALLOCATION_BUTTON_LABEL,
  MAKE_FINAL_ALLOCATION_CONFIRMATION_MESSAGE,
  PUBLISH_ALLOCATION_BUTTON_LABEL,
  PUBLISH_ALLOCATION_CONFIRMATION_MESSAGE,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { useMakeAllocationsFinal, usePublishAllocationVersion } from 'services/hooks/allocations';
import { useSetReadyForAudit } from 'services/hooks/valuations';
import { getNumberValue, getObjectValue, handleCatchError } from 'utilities';

const useValuationStatusManagement = ({
  allocationVersion,
  user,
  firmId,
  getValuationInfo,
  getVersions,
  isFirmOwned,
  valuation,
  setCurrentAllocationStatus,
  setIsCurrentAllocationFinal,
  setIsCurrentAllocationPublished,
  setPageStatus,
  setValuation,
  showDialog,
}: {
  allocationVersion: AllocationName;
  user?: User | null;
  firmId?: number | null;
  getValuationInfo: Refetch<QueryData<ValuationByAllocation>, QueryError>;
  getVersions?: (version: AllocationName) => void;
  isFirmOwned: boolean;
  valuation: ValuationByAllocation;
  setCurrentAllocationStatus: (currentAllocationStatus: 0 | 1 | -1 | null) => void;
  setIsCurrentAllocationPublished: React.Dispatch<React.SetStateAction<boolean>>;
  setIsCurrentAllocationFinal: React.Dispatch<React.SetStateAction<boolean>>;
  setPageStatus: React.Dispatch<React.SetStateAction<string>>;
  setValuation: React.Dispatch<React.SetStateAction<ValuationByAllocation | null>>;
  showDialog: (params: ShowDialog) => void;
}) => {
  const { is_ready_for_audit: isValuationReadyForAudit, allocation_id: allocationId } = valuation;
  const { mutateAsync: makeFinalAllocationVersion } = useMakeAllocationsFinal();
  const { mutateAsync: publishAllocationVersion } = usePublishAllocationVersion();
  const { mutateAsync: setReadyForAudit } = useSetReadyForAudit();
  const isReadyForAudit = useMemo(() => isValuationReadyForAudit ?? false, [isValuationReadyForAudit]);

  // Publish Allocation
  const handlePublishAllocation = useCallback(async () => {
    const publishedAllocation = await publishAllocationVersion({ allocationId });
    const { published } = getObjectValue(publishedAllocation);

    if (published) {
      getValuationInfo().catch(handleCatchError);
      setCurrentAllocationStatus(ALLOCATIONS_PUBLISHED);
      setIsCurrentAllocationPublished(true);

      // Update the version list so the status change is reflected in the dropdown
      getVersions?.({
        ...allocationVersion,
        is_published: true,
      });
    }
  }, [
    allocationId,
    allocationVersion,
    getValuationInfo,
    getVersions,
    publishAllocationVersion,
    setCurrentAllocationStatus,
    setIsCurrentAllocationPublished,
  ]);

  // Publish Allocation Dialog
  const showPublishAllocationDialog = useCallback(() => {
    showDialog({
      actions: [
        {
          callback: handlePublishAllocation,
          label: PUBLISH_ALLOCATION_BUTTON_LABEL,
          type: 'success',
          variant: 'contained',
        },
      ],
      content: CenterAlignedParagraph,
      contentProps: { message: PUBLISH_ALLOCATION_CONFIRMATION_MESSAGE },
      wrapper: ConfirmationDialog,
    });
  }, [handlePublishAllocation, showDialog]);

  // Make Final Allocation
  const handleMakeFinalAllocation = useCallback(async () => {
    const finalAllocation = await makeFinalAllocationVersion({ allocationsIds: [getNumberValue(allocationId)] });
    const { updated } = getObjectValue(finalAllocation);

    if (updated) {
      getValuationInfo().catch(handleCatchError);
      setCurrentAllocationStatus(ALLOCATIONS_FINAL);
      setIsCurrentAllocationFinal(true);

      // Update the version list so the status change is reflected in the dropdown
      getVersions?.({
        ...allocationVersion,
        is_final: true,
      });
    }
  }, [
    allocationId,
    allocationVersion,
    getValuationInfo,
    getVersions,
    makeFinalAllocationVersion,
    setCurrentAllocationStatus,
    setIsCurrentAllocationFinal,
  ]);

  // Make Final Allocation Dialog
  const showMakeFinalAllocationDialog = useCallback(() => {
    showDialog({
      actions: [
        {
          callback: handleMakeFinalAllocation,
          label: MAKE_FINAL_ALLOCATION_BUTTON_LABEL,
          type: 'success',
          variant: 'contained',
        },
      ],
      content: CenterAlignedParagraph,
      contentProps: { message: MAKE_FINAL_ALLOCATION_CONFIRMATION_MESSAGE },
      wrapper: ConfirmationDialog,
    });
  }, [handleMakeFinalAllocation, showDialog]);

  const changeValuationOwnership = useCallback(() => {
    const updatedValuation = { ...valuation, is_firm_owned: !isFirmOwned };
    setValuation(updatedValuation);

    // Save Valuation to avoid modifications from not Scalar users
    if (updatedValuation) {
      setPageStatus(VALIDATING);
    }
  }, [isFirmOwned, valuation, setValuation, setPageStatus]);

  const toggleReadyForAudit = useCallback(async () => {
    const requestData = {
      valuationId: valuation.id!,
      readyForAuditData: { is_ready_for_audit: !isReadyForAudit },
    };
    const response = await setReadyForAudit(requestData);
    if (response) {
      const updatedValuation = { ...valuation, is_ready_for_audit: response.is_ready_for_audit };
      setValuation(updatedValuation);
    }
  }, [valuation, isReadyForAudit, setReadyForAudit, setValuation]);

  const userCanToggleAuditState = useMemo(() => {
    const currentFirmRole = user?.firms_permissions?.find(perm => perm.id === firmId)
      ?.user_firm_role as unknown as number;
    const validRoleAccess = [FIRM_ADMIN_VALUE, FUND_ADMIN_VALUE, ANALYST_VALUE].includes(currentFirmRole);
    return user?.is_superuser || user?.is_staff || validRoleAccess;
  }, [user, firmId]);

  return {
    changeValuationOwnership,
    showMakeFinalAllocationDialog,
    showPublishAllocationDialog,
    userCanToggleAuditState,
    toggleReadyForAudit,
  };
};

export default useValuationStatusManagement;
